import { useTheme } from '../theme';

const ROOM_MESSAGES: Record<string, { emoji: string; text: string }> = {
  living_room: { emoji: '🛋️', text: 'Die Couch ruft — aber hier ist erstmal Ruhe.' },
  kitchen: { emoji: '🍳', text: 'Küche ist sauber, keine Geräte gemeldet.' },
  bedroom: { emoji: '🛏️', text: 'Schlafzimmer im Standby — Süße Träume warten.' },
  bathroom: { emoji: '🛁', text: 'Bad ist bereit — nur noch keine Geräte hier.' },
  garage: { emoji: '🚗', text: 'Garage ist ruhig — alles gut verstaut.' },
  office: { emoji: '💻', text: 'Büro ist aufgeräumt, keine Aktivität.' },
  hallway: { emoji: '🚪', text: 'Der Flur ist leer — hier passiert gerade nichts.' },
  garden: { emoji: '🌳', text: 'Der Garten schläft — keine Sensoren aktiv.' },
  basement: { emoji: '📦', text: 'Keller ist ruhig — alles wo es sein soll.' },
};

const DEFAULT_MESSAGE = { emoji: '📭', text: 'Noch keine Geräte in diesem Raum.' };

function messageForRoom(areaId?: string, areaName?: string): { emoji: string; text: string } {
  if (!areaId && !areaName) return DEFAULT_MESSAGE;
  const key = (areaId ?? areaName ?? '').toLowerCase().replace(/\s+/g, '_');
  for (const room of Object.keys(ROOM_MESSAGES)) {
    if (key.includes(room)) return ROOM_MESSAGES[room];
  }
  return DEFAULT_MESSAGE;
}

export interface EmptyRoomStateProps {
  areaId?: string;
  areaName?: string;
}

export function EmptyRoomState({ areaId, areaName }: EmptyRoomStateProps): JSX.Element {
  const { theme } = useTheme();
  const { emoji, text } = messageForRoom(areaId, areaName);

  return (
    <div
      className={`rsc-empty-room flex flex-col items-center gap-2 rounded-2xl border-2 border-dashed p-6 text-center ${
        theme.isDark ? 'border-gray-700 text-gray-400' : 'border-gray-200 text-gray-500'
      }`}
      data-testid="empty-room"
    >
      <div className="text-3xl" aria-hidden="true">
        {emoji}
      </div>
      <div className="text-sm">{text}</div>
    </div>
  );
}

export interface EmptyDashboardStateProps {
  reason?: 'no-rooms' | 'disconnected' | 'connecting' | 'auth-invalid';
  onRetry?: () => void;
}

export function EmptyDashboardState({ reason = 'no-rooms', onRetry }: EmptyDashboardStateProps): JSX.Element {
  const { theme } = useTheme();
  const isDark = theme.isDark;

  if (reason === 'auth-invalid') {
    return (
      <div
        className={`rsc-empty-dashboard mx-auto flex max-w-md flex-col items-center gap-4 rounded-2xl border p-8 text-center ${
          isDark ? 'bg-gray-800 border-gray-700 text-gray-200' : 'bg-white border-gray-200 text-gray-700 shadow-sm'
        }`}
        data-testid="empty-auth-invalid"
      >
        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-rose-500/20 text-2xl" aria-hidden="true">
          🔑
        </div>
        <div>
          <div className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
            Anmeldung abgelaufen
          </div>
          <div className={`mt-1 text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            Deine Home-Assistant-Sitzung ist abgelaufen oder wurde widerrufen. Bitte melde dich erneut an.
          </div>
        </div>
        {onRetry && (
          <button
            type="button"
            onClick={onRetry}
            className="mt-2 rounded-lg bg-[var(--rsc-primary)] px-4 py-2 text-sm font-semibold text-white shadow-md transition hover:brightness-110"
          >
            Erneut anmelden
          </button>
        )}
      </div>
    );
  }

  if (reason === 'disconnected') {
    return (
      <div
        className={`rsc-empty-dashboard mx-auto flex max-w-md flex-col items-center gap-4 rounded-2xl border p-8 text-center ${
          isDark ? 'bg-gray-800 border-gray-700 text-gray-200' : 'bg-white border-gray-200 text-gray-700 shadow-sm'
        }`}
        data-testid="empty-disconnected"
      >
        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-rose-500/20 text-2xl" aria-hidden="true">
          ⚡
        </div>
        <div>
          <div className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
            Home Assistant nicht erreichbar
          </div>
          <div className={`mt-1 text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            Wir versuchen, die Verbindung wiederherzustellen. Prüfe die Konfiguration
            oder starte einen Retry.
          </div>
        </div>
        {onRetry && (
          <button
            type="button"
            onClick={onRetry}
            className="mt-2 rounded-lg bg-[var(--rsc-primary)] px-4 py-2 text-sm font-semibold text-white shadow-md transition hover:brightness-110"
          >
            Neu verbinden
          </button>
        )}
      </div>
    );
  }

  if (reason === 'connecting') {
    return (
      <div
        className={`rsc-empty-dashboard mx-auto flex max-w-md flex-col items-center gap-4 rounded-2xl border p-8 text-center ${
          isDark ? 'bg-gray-800 border-gray-700 text-gray-200' : 'bg-white border-gray-200 text-gray-700 shadow-sm'
        }`}
        data-testid="empty-connecting"
      >
        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-sky-500/20 text-2xl" aria-hidden="true">
          🔄
        </div>
        <div>
          <div className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
            Verbindung wird aufgebaut…
          </div>
          <div className={`mt-1 text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            Wir laden gerade deine Räume und Geräte.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`rsc-empty-dashboard mx-auto flex max-w-md flex-col items-center gap-4 rounded-2xl border p-8 text-center ${
        isDark ? 'bg-gray-800 border-gray-700 text-gray-200' : 'bg-white border-gray-200 text-gray-700 shadow-sm'
      }`}
      data-testid="empty-no-rooms"
    >
      <div className="flex h-14 w-14 items-center justify-center rounded-full bg-[var(--rsc-primary)]/15 text-2xl" aria-hidden="true">
        🏠
      </div>
      <div>
        <div className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
          Willkommen im RSC hub
        </div>
        <div className={`mt-1 text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
          Wir haben noch keine Räume gefunden. Verbinde Home Assistant, damit deine
          Areas und Entities hier erscheinen.
        </div>
      </div>
    </div>
  );
}
